import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProducts, type ProductKey } from "@/hooks/useEntitlements";
import { cn } from "@/lib/utils";
import CheckoutDialog from "./CheckoutDialog";

/** Latest pending checkout link for a product (lets the user resume a closed payment). */
export function usePendingCheckout(productKey: ProductKey, enabled = true) {
  const { user } = useAuth();
  return useQuery<string | null>({
    queryKey: ["pending-checkout", user?.id, productKey],
    enabled: enabled && !!user?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("payment_intents")
        .select("checkout_url")
        .eq("user_id", user!.id)
        .eq("product_key", productKey)
        .eq("status", "pending")
        .not("checkout_url", "is", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data?.checkout_url ?? null;
    },
    staleTime: 15_000,
  });
}

interface Props {
  productKey: ProductKey;
  compact?: boolean;
  className?: string;
  onBeforeNewCheckout?: () => void;
}

export default function PendingPaymentActions({ productKey, compact, className, onBeforeNewCheckout }: Props) {
  const { data: url } = usePendingCheckout(productKey);
  const { data: products } = useProducts();
  const product = products?.find((p) => p.key === productKey) ?? null;
  const [open, setOpen] = useState(false);

  return (
    <div className={cn("flex flex-col items-stretch gap-1.5 w-full", className)}>
      <span className={cn("text-center text-muted-foreground", compact ? "text-[11px]" : "text-xs")}>
        Aguardando confirmação do pagamento
      </span>
      {url && (
        <Button
          size={compact ? "sm" : "default"}
          className={cn("rounded-full", compact && "h-8 text-[11px]")}
          onClick={() => window.open(url, "_blank", "noopener")}
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
          Continuar pagamento
        </Button>
      )}
      <button
        type="button"
        onClick={() => {
          onBeforeNewCheckout?.();
          setOpen(true);
        }}
        className={cn(
          "underline underline-offset-2 text-muted-foreground hover:text-foreground transition-colors",
          compact ? "text-[11px]" : "text-xs",
        )}
      >
        Gerar novo pagamento
      </button>
      <CheckoutDialog product={product} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
